import React from 'react';
import { keyframes } from '@emotion/react';
import * as NavigationMenu from '../primitives/navigation-menu';
import Container from '../primitives/grid/container';
import { colors } from '../primitives/colors';
import Link from '../primitives/link';
import Logo from '../primitives/logo';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import { useCtaProvider } from '../../contexts/franchise/cta-provider';
import { usePrimaryNav } from '../../hooks/use-primary-nav';
import Caret from './caret';
import Typography from '../primitives/typography';
import SvgSafeGatsbyImage from '../atoms/SvgSafeGatsbyImage';
import DropdownMenu from '../primitives/dropdown-menu';
import ColSet from '../primitives/grid/col-set';
import Skeleton from '../primitives/skeleton';
import PhoneNumber from '../molecules/phone-number';
import { useLocator } from '../../contexts/franchise/locator-provider';
import ArrowDropdown from '../../images/svg/arrow-header-dropdown.svg';
import HeaderDropdown from './header-dropdown';
import { NATIONAL_CALL_CENTER_DISPLAY, PHONE_NUMBER, PHONE_NUMBER_DISPLAY } from '../../constants/constants';
import formatPhoneLink from '../utils/format-phone-link';
import { breakpoints } from '../primitives/tokens';
import FadeTransition from '../animations/transitions/fade-transition';

const fadeIn = keyframes({
    from: { opacity: 0 },
    to: { opacity: 1 },
});

const fadeOut = keyframes({
    from: { opacity: 1 },
    to: { opacity: 0 },
});

interface DropdownMenuProps {
    text: string
    phoneNumber?: string
    phoneDisplay?: string
}

const StyledPhoneNumber = ({ children, href, ...props }): JSX.Element => (
    <Typography
        fontSize={{
            dt: 'lg',
        }}
        css={{
            color: colors.primaryOrange,
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
        }}
        {...props}
    >
        <span css={{ lineHeight: 1 }}>
            <PhoneNumber href={href}>{children}</PhoneNumber>
        </span>
    </Typography>
);

const StyledPhoneBlock = ({
    text,
    phoneNumber,
    phoneDisplay,
}: DropdownMenuProps): JSX.Element => (
    <ColSet
        breakpoints={{
            dt: { between: 10 },
        }}
        css={{
            display: 'flex',
            position: 'relative',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 'auto',
            width: 'auto',
        }}
    >
        <Typography
            fontSize="sm"
            css={{
                textAlign: 'right',
                whiteSpace: 'nowrap',
            }}
        >
            <span css={{ lineHeight: 1.1 }}>{text}</span>
            {phoneNumber && (
                <StyledPhoneNumber
                    href={`tel:${phoneNumber}`}
                >
                    {formatPhoneLink(phoneNumber || phoneDisplay)}
                </StyledPhoneNumber>
            )}
        </Typography>
    </ColSet>
);

const StyledDropdown = ({
    text,
    phoneNumber,
    phoneDisplay,
}: DropdownMenuProps): JSX.Element => (
    <DropdownMenu
        align="end"
        title={(
            <ColSet
                breakpoints={{
                    dt: { between: 10 },
                }}
                css={{
                    display: 'flex',
                    position: 'relative',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 'auto',
                }}
            >
                <Typography
                    fontSize="sm"
                    css={{
                        textAlign: 'right',
                    }}
                >
                    <span css={{ lineHeight: 1.1 }}>{text}</span>
                    <Skeleton>
                        {phoneNumber && (
                            <StyledPhoneNumber
                                href={`tel:${phoneNumber}`}
                            >
                                {formatPhoneLink(phoneNumber || phoneDisplay)}
                            </StyledPhoneNumber>
                        )}
                    </Skeleton>
                </Typography>
                <ArrowDropdown
                    css={{
                        transition: '0.2s',
                        '[data-state="open"] &': {
                            transform: 'rotate(180deg)',
                        },
                    }}
                />
            </ColSet>
        )}
    >
        <HeaderDropdown />
    </DropdownMenu>
);

const getColumnWidthAndOffset = (
    columnCount: number,
    columnGapPixels: number,
    subgroupLength: number,
): {
    widthPercent: number,
    offsetPixels: number
} => {
    if (subgroupLength > columnCount) {
        // We're subdividing to fit the space available
        const totalGapSize = columnGapPixels * (subgroupLength - 1); // The column count - 1, because we only care about the spaces
        const individualGapDecrement = totalGapSize / subgroupLength; // The width adjustment per col for even gapping
        const colPcSize = 100 / subgroupLength;
        return {
            widthPercent: colPcSize,
            offsetPixels: individualGapDecrement,
        };
    }

    const totalGapSize = columnGapPixels * (columnCount - 1); // The column count - 1, because we only care about the spaces
    const individualGapDecrement = totalGapSize / columnCount; // The width adjustment per col for even gapping

    const columnsInItem = Math.max(1, subgroupLength);

    const colPcSize = 100 / columnCount;
    const columnWidth = Math.min(columnsInItem * colPcSize, columnCount * colPcSize); // Max it out at the max column count

    const skippedColumnIncr = columnGapPixels / 4; // The unit we need to use to compensate for the gap not existing, when columns > 1

    const offsetPx = individualGapDecrement - (skippedColumnIncr * columnsInItem) + skippedColumnIncr;

    return {
        widthPercent: columnWidth,
        offsetPixels: offsetPx,
    };
};

const getHeaderContentWidth = (maxDropdownSize: number, childMenuLength: number, maxAvailableColumns: number): string => `min(${Math.max(1, Math.min(childMenuLength === (maxAvailableColumns - 1) ? maxAvailableColumns : childMenuLength, maxAvailableColumns)) * (maxDropdownSize / maxAvailableColumns)}px, ${Math.max(1, Math.min(childMenuLength === (maxAvailableColumns - 1) ? maxAvailableColumns : childMenuLength, maxAvailableColumns)) * (98.5 / maxAvailableColumns)}vw)`;

const NavItem = ({
    to,
    className,
    children,
}: React.PropsWithChildren<{
    to?: string
    className?: string
}>): JSX.Element => {
    let element;

    if (to) {
        element = (
            <Link
                to={to}
                className={className}
                css={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    lineHeight: '1',
                }}
            >
                {children}
            </Link>
        );
    } else {
        element = (
            <div
                className={className}
                css={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    lineHeight: '1',
                }}
            >
                {children}
            </div>
        );
    }

    return (
        <Typography
            fontSize={{
                dt: 'xl',
            }}
            fontWeight="regular"
            css={{
                color: colors.grey800,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                transition: 'color 0.2s',
                whiteSpace: 'nowrap',
                svg: {
                    transition: 'transform 0.3s',
                },
                '[data-state="open"] &': {
                    color: colors.primaryOrange,
                    svg: {
                        transform: 'rotate(180deg)',
                    },
                },
            }}
        >
            {element}
        </Typography>
    );
};

const FirstLevelChild = ({ item }): JSX.Element => {
    const { category_link, icon, subgroup = [] } = item;
    const { href, title } = category_link;

    return (
        <div>
            <Link
                to={href}
                role="group"
                css={{
                    display: 'flex',
                    gap: '6px',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingBottom: '5px',
                    borderBottom: `1px solid ${colors.grey200}`,
                    transition: '0.2s',
                    '&:hover': {
                        borderBottom: `1px solid ${colors.primaryOrange}`,
                    },
                }}
            >
                {icon && (
                    <SvgSafeGatsbyImage
                        css={{
                            width: '32px',
                            height: '32px',
                        }}
                        image={icon}
                    />
                )}
                <Typography
                    fontSize={{
                        dt: 'xl',
                    }}
                    color="shadesBlack"
                    css={{
                        transition: '0.2s',
                        '[role="group"]:hover &': {
                            color: colors.primaryOrange,
                        },
                    }}
                >
                    {title}
                </Typography>
            </Link>
            {subgroup.length > 0 && (
                <div
                    css={{
                        display: 'flex',
                        flexFlow: 'wrap',
                        width: '100%',
                        columnGap: '36px',
                    }}
                    style={{
                        marginLeft: icon ? '38px' : '0', // 32px + 6px gap
                        marginTop: '7px',
                    }}
                >
                    {subgroup.map((subgroupItem) => {
                        const { subgroup_link, links } = subgroupItem;

                        const { title } = subgroup_link;

                        const {
                            widthPercent,
                            offsetPixels,
                        } = getColumnWidthAndOffset(1, 36, subgroup.length);

                        if (title) {
                            // This should be treated as a nested subgroup

                            return (
                                <div css={{
                                    width: `calc(${widthPercent}% - ${offsetPixels}px)`,
                                }}
                                >
                                    <Typography
                                        fontSize={{
                                            dt: 'lg',
                                        }}
                                        fontWeight="medium"
                                    >
                                        {title}
                                    </Typography>
                                    <ul css={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '7px',
                                        marginTop: '7px',
                                    }}
                                    >
                                        {links.map((link) => {
                                            if (!link) return null;

                                            const { href, title } = link;

                                            return (
                                                <li>
                                                    <Typography
                                                        as={Link}
                                                        to={href}
                                                        fontSize={{
                                                            dt: 'lg',
                                                        }}
                                                        css={{
                                                            display: 'inline-block',
                                                            width: '100%',
                                                            transition: '0.2s',
                                                            '&:hover': {
                                                                color: colors.primaryOrange,
                                                            },
                                                        }}
                                                    >
                                                        {title}
                                                    </Typography>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        }

                        return (
                            <ul css={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '7px',
                                width: `calc(${widthPercent}% - ${offsetPixels}px)`,
                            }}
                            >
                                {links.map((link) => {
                                    if (!link) return null;

                                    const { href, title } = link;

                                    return (
                                        <li css={{
                                            width: '100%',
                                        }}
                                        >
                                            <Typography
                                                as={Link}
                                                to={href}
                                                fontSize={{
                                                    dt: 'lg',
                                                }}
                                                css={{
                                                    display: 'inline-block',
                                                    width: '100%',
                                                    transition: '0.2s',
                                                    '&:hover': {
                                                        color: colors.primaryOrange,
                                                    },
                                                }}
                                            >
                                                {title}
                                            </Typography>
                                        </li>
                                    );
                                })}
                            </ul>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const MenuItem = ({ item }): JSX.Element => {
    const { nav_item_link, columns = [] } = item;
    const { href, title } = nav_item_link;

    const { isMicrosite } = useMicrosite();
    const headerBreakpointForTablet = isMicrosite ? breakpoints.tb : breakpoints.headerTb;

    const childMenu = columns.flatMap(x => x.group).filter(x => !!x);

    if (!(childMenu.length > 0)) {
        return (
            <NavItem
                to={href}
                css={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {title}
            </NavItem>
        );
    }

    return (
        <>
            <NavigationMenu.Trigger
                css={{
                    display: 'flex',
                    select: 'none',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '100%',
                }}
            >
                <NavItem>
                    {title}
                    <Caret css={{
                        height: '5px',
                        marginBottom: '4px',
                        marginLeft: '5px',
                    }}
                    />
                </NavItem>
            </NavigationMenu.Trigger>
            <NavigationMenu.Content
                animationTimeMs={400}
                css={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: 'auto',
                }}
                mode={childMenu.length > 2 ? 'center' : 'self'}
            >
                <div
                    css={{
                        margin: '0',
                        display: 'grid',
                        listStyleType: 'none',
                        width: getHeaderContentWidth(1313, childMenu.length, 4),
                        [headerBreakpointForTablet]: {
                            width: getHeaderContentWidth(1313, childMenu.length, 2),
                        },
                    }}
                >
                    <Container
                        canStack
                        css={{
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <div css={{
                            margin: '38px 0',
                            width: '100%',
                            maxWidth: '1193px',
                            /*
                            * 1313 - 60 * 2 (largest margin) = 1193
                            * This is being done so that when the dropdown
                            * is full size, the margin aligns with the logo
                            * and lower content.
                            * */
                            [headerBreakpointForTablet]: {
                                margin: '28px 0',
                            },
                        }}
                        >
                            <ul css={{
                                display: 'flex',
                                flexFlow: 'wrap',
                                width: '100%',
                                columnGap: '40px',
                                rowGap: '47px',
                                [headerBreakpointForTablet]: {
                                    rowGap: '15px',
                                    columnGap: '33px',
                                },
                            }}
                            >
                                {childMenu.map((node) => {
                                    const childSubgroupLength = (childMenu || []).filter(x => !!x).map((x) => {
                                        if (x.subgroup && x.subgroup.filter(x => !!x).length > 0) {
                                            return x.subgroup.filter(x => !!x).length;
                                        }
                                        return 1;
                                    }).reduce((acc, val) => acc + val, 0);

                                    const {
                                        widthPercent,
                                        offsetPixels,
                                    } = getColumnWidthAndOffset(
                                        Math.min(Math.max(childSubgroupLength, 1), 4),
                                        40,
                                        node.subgroup.length,
                                    );

                                    const {
                                        widthPercent: widthPercentTb,
                                        offsetPixels: offsetPixelsTb,
                                    } = getColumnWidthAndOffset(
                                        Math.min(Math.max(childSubgroupLength, 2), 2),
                                        40,
                                        node.subgroup.length,
                                    );

                                    return (
                                        <div
                                            css={{
                                                width: `calc(${widthPercent}% - ${offsetPixels}px)`,
                                                [headerBreakpointForTablet]: {
                                                    width: `calc(${widthPercentTb}% - ${offsetPixelsTb}px)`,
                                                },
                                            }}
                                            key={node.category_link.title}
                                        >
                                            <FirstLevelChild
                                                item={node}
                                            />
                                        </div>
                                    );
                                })}
                            </ul>
                        </div>
                    </Container>
                </div>
            </NavigationMenu.Content>
        </>
    );
};

const DesktopHeader = (): JSX.Element => {
    const { nav_items: primaryNavLinks } = usePrimaryNav(); // These need to be typed

    const {
        isLocalCityPage,
        isMicrosite,
        permalink: micrositePermalink,
        franchise: micrositeFranchise,
        menu: micrositeMenu, // These need to be typed
    } = useMicrosite();

    const {
        hideNationalCallCenter,
        hideFranchiseLink,
        overridePhoneNumber,
        overridePhoneNumberDescriptor,
    } = useCtaProvider();

    const { franchise: locatorFranchise, isNationalCallCenter } = useLocator();
    const franchise = micrositeFranchise || locatorFranchise?.data;

    const menuToUse = (isMicrosite && micrositeMenu) ? micrositeMenu.nav_items : primaryNavLinks;
    const headerBreakpointForTablet = isMicrosite ? breakpoints.tb : breakpoints.headerTb;

    return (
        <NavigationMenu.Root
            css={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                backgroundColor: colors.shadesWhite,
            }}
            delayDuration={100}
        >
            <Container css={{
                backgroundColor: colors.shadesWhite,
            }}
            >
                <NavigationMenu.List css={{
                    display: 'flex',
                    listStyleType: 'none',
                    justifyContent: 'space-between',
                    alignItems: 'stretch',
                    gap: '20px',
                    [headerBreakpointForTablet]: {
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gridTemplateRows: '1fr 1fr',
                        gap: '0px 0px',
                        gridTemplateAreas: '"logo cta""menu menu"',
                        paddingTop: '8px',
                    },
                }}
                >
                    <NavigationMenu.Item css={{
                        position: 'relative',
                        zIndex: 10,
                        [headerBreakpointForTablet]: {
                            gridArea: 'logo',
                            display: 'flex',
                            justifyContent: 'flex-start',
                        },
                    }}
                    >
                        <div css={{
                            display: 'flex',
                            padding: '11px 0',
                            justifyContent: 'flex-start',
                            height: '100%',
                            transition: '0.2s',
                            width: '190px',
                            willChange: 'width',
                            '.top-bar &': {
                                width: '250px', // Accounts for larger scale
                            },
                            '.top-bar .scrolled &': {
                                width: '190px',
                            },
                            [headerBreakpointForTablet]: {
                                padding: '4px 0',
                            },
                        }}
                        >
                            <Link
                                to={(isMicrosite && micrositePermalink) ? micrositePermalink : '/'}
                                css={{
                                    alignSelf: 'center',
                                    position: 'relative',
                                }}
                                aria-label="SERVPRO"
                            >
                                <Logo
                                    css={{
                                        width: '184px',
                                        transition: '0.2s',
                                        transformOrigin: 'bottom left',
                                        willChange: 'transform',
                                        '.top-bar &': {
                                            transform: 'scale(1.3) translateY(-8px)',
                                        },
                                        '.top-bar .scrolled &': {
                                            transform: 'scale(1)',
                                        },
                                    }}
                                />
                            </Link>
                        </div>
                    </NavigationMenu.Item>
                    <div css={{
                        display: 'flex',
                        listStyleType: 'none',
                        justifyContent: 'flex-start',
                        alignItems: 'stretch',
                        gap: '24px',
                        width: '100%',
                        height: '54px',
                        marginTop: 'auto',
                        willChange: 'height',
                        transition: '0.2s',
                        '.top-bar &': {
                            height: '66px',
                            [headerBreakpointForTablet]: {
                                height: '56px',
                            },
                        },
                        '.top-bar .scrolled &': {
                            height: '54px',
                        },
                        [headerBreakpointForTablet]: {
                            gridArea: 'menu',
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            height: 'unset',
                            marginTop: 'unset',
                        },
                    }}
                    >
                        {menuToUse && menuToUse.map((item) => {
                            const { nav_item_link, columns = [] } = item;
                            if (!nav_item_link) return null;
                            const { href } = nav_item_link;

                            if (!href && columns.filter(x => x.group.filter(x => !!x).length > 0).length === 0) return null;

                            return (
                                <NavigationMenu.Item key={item.id}>
                                    <MenuItem item={item} />
                                </NavigationMenu.Item>
                            );
                        })}
                    </div>
                    <NavigationMenu.Item css={{
                        position: 'relative',
                        zIndex: 10,
                        width: '100%',
                        [headerBreakpointForTablet]: {
                            gridArea: 'cta',
                            display: 'flex',
                            justifyContent: 'flex-end',
                        },
                    }}
                    >
                        <div css={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            maxWidth: '100%',
                            height: '100%',
                            padding: '14px 0',
                            [headerBreakpointForTablet]: {
                                padding: '0',
                            },
                        }}
                        >
                            <ColSet
                                breakpoints={{ dt: { between: 12 } }}
                                css={{
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <FadeTransition shouldChange={((!hideFranchiseLink
                                        && !hideNationalCallCenter
                                        && !isMicrosite
                                        && !isNationalCallCenter)
                                    || (isLocalCityPage && !hideNationalCallCenter && !hideFranchiseLink)).toString()}
                                >
                                    {!isMicrosite
                                        && !hideFranchiseLink
                                        && !isNationalCallCenter
                                        && (locatorFranchise?.isLoading || !locatorFranchise) && (
                                        <StyledDropdown
                                            text="Finding your Local SERVPRO"
                                        />
                                    )}
                                    {(
                                        franchise
                                        && ((!hideFranchiseLink && !isMicrosite && !isNationalCallCenter)
                                            || (isLocalCityPage && !hideFranchiseLink))
                                    ) && (
                                        <StyledDropdown
                                            text="Your Local SERVPRO"
                                            phoneNumber={franchise.mainPhone}
                                            phoneDisplay={franchise.mainPhone}
                                            localCtaData={null}
                                        />
                                    )}
                                </FadeTransition>
                                <div css={{
                                    width: '8px',
                                    height: '46px',
                                }}
                                >
                                    <FadeTransition shouldChange={((!hideFranchiseLink
                                            && !hideNationalCallCenter
                                            && !isMicrosite
                                            && !isNationalCallCenter)
                                        || (isLocalCityPage && !hideNationalCallCenter && !hideFranchiseLink)).toString()}
                                    >
                                        {((!hideFranchiseLink
                                                && !hideNationalCallCenter
                                                && !isMicrosite
                                                && !isNationalCallCenter)
                                            || (isLocalCityPage && !hideNationalCallCenter && !hideFranchiseLink)
                                        ) && (
                                            <hr
                                                aria-orientation="vertical"
                                                css={{
                                                    height: '46px',
                                                    display: 'block',
                                                    backgroundColor: colors.grey200,
                                                    width: '1px',
                                                }}
                                            />
                                        )}
                                    </FadeTransition>
                                </div>
                                <div>
                                    {(overridePhoneNumber && overridePhoneNumberDescriptor) && (
                                        <StyledPhoneBlock
                                            text={overridePhoneNumberDescriptor}
                                            phoneDisplay={overridePhoneNumber}
                                            phoneNumber={overridePhoneNumber}
                                        />
                                    )}
                                    {((!hideNationalCallCenter && !isMicrosite)
                                        || (isLocalCityPage && !hideNationalCallCenter)
                                    ) && (
                                        <StyledPhoneBlock
                                            text={NATIONAL_CALL_CENTER_DISPLAY}
                                            phoneDisplay={PHONE_NUMBER_DISPLAY}
                                            phoneNumber={PHONE_NUMBER}
                                        />
                                    )}
                                    {(isMicrosite && franchise) && (
                                        <StyledPhoneBlock
                                            text="Call Today - 24/7 Emergency Services"
                                            phoneDisplay={franchise?.tempOutOfService ? PHONE_NUMBER_DISPLAY : formatPhoneLink(franchise.mainPhone)}
                                            phoneNumber={franchise?.tempOutOfService ? PHONE_NUMBER : franchise.mainPhone}
                                        />
                                    )}
                                </div>
                            </ColSet>
                        </div>
                    </NavigationMenu.Item>
                    <NavigationMenu.Indicator css={{
                        top: 'calc(100% - 3px)', // Overlap with the bottom grey border
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'end',
                        justifyContent: 'center',
                        transition: '300ms ease',
                        '&[data-state="unset"]': {
                            opacity: 0,
                            transform: 'translateX(250px)', // Start it at roughly logo offset
                        },
                        '&[data-state="hidden"]': {
                            animation: `${fadeOut} 200ms ease forwards`,
                        },
                        '&[data-state="visible"]': {
                            animation: `${fadeIn} 200ms ease forwards`,
                        },
                    }}
                    >
                        <div css={{
                            position: 'relative',
                            backgroundColor: colors.primaryOrange,
                            height: '4px',
                            width: '100%',
                            borderTopLeftRadius: '3px',
                            borderTopRightRadius: '3px',
                        }}
                        />
                    </NavigationMenu.Indicator>
                </NavigationMenu.List>
            </Container>
            <div css={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
            }}
            >
                <div css={{
                    perspective: '2000px',
                    position: 'absolute',
                    // left: '0',
                    top: '100%',
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'flex-end',
                    maxWidth: '1313px',
                }}
                >
                    <NavigationMenu.Viewport
                        css={{
                            overflowY: 'auto',
                            maxHeight: 'calc(100vh - 200px)',
                            WebkitOverflowScrolling: 'touch',
                            boxShadow: '0 4px 4px 0px #00000040',
                            backgroundColor: colors.shadesWhite,
                            top: '0px',
                            position: 'relative',
                            height: 'var(--menu-viewport-height)',
                            // width: '100%',
                            width: 'var(--menu-viewport-width)',
                            maxWidth: '1313px',
                            transformOrigin: 'top center',
                            overflowX: 'hidden',
                            transitionProperty: 'height', // This previously had width - but too jumpy when going to 1 col from 4
                            transitionDuration: '0.5s',
                            transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)',
                            '&[data-state="open"]': {
                                animation: `${fadeIn} 0.3s ease forwards`,
                                borderTop: `1px solid ${colors.primaryOrange}`,
                            },
                            '&[data-state="closed"]': {
                                animation: `${fadeOut} 0.3s ease forwards`,
                                borderTop: `1px solid ${colors.shadesWhite}`,
                            },
                            '&[data-menu-mode="self"]': {
                                transform: 'translateX(var(--menu-viewport-self-x))',
                            },
                            '&[data-menu-mode="center"]': {
                                transform: 'translateX(var(--menu-viewport-center-x))',
                            },
                            [headerBreakpointForTablet]: {
                                maxHeight: 'calc(100vh - 236px)',
                                '&[data-menu-mode="self"]': {
                                    transform: 'translateX(0)',
                                    width: '100%',
                                },
                            },
                        }}
                    />
                </div>
            </div>
        </NavigationMenu.Root>
    );
};

export default DesktopHeader;
